import React from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import {
  InteractiveMapItemInterface,
  InteractiveMapListInterface,
  ProjectIdentity,
  RootStateTypeExtra,
  TabInterface,
} from '@store/types'

import {
  InteractiveMapItem,
  InteractiveMapTab,
} from '@components/interactive-map-control/elements'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import useScrollEffect from '@hooks/useScrollEffect'

import { ProjectType } from '@adUtilities/constants/common'

import InteractiveMapItemHnl from './elements/interactive-map-item-hnl'

export interface InteractiveMapHandlerInterface {
  interactiveMapList: Array<InteractiveMapListInterface>
  interactiveMapSession: string[]
  interactiveMapActiveCategory:
    | { label: string; baseImageOverride: string }
    | undefined
  projectIdentity: ProjectIdentity
  storyMode: boolean
}

interface InteractiveMapTabInterface extends TabInterface {
  activeItem: number
}

function InteractiveMapHandler({
  interactiveMapList,
  interactiveMapSession,
  interactiveMapActiveCategory,
  projectIdentity,
  storyMode,
}: InteractiveMapHandlerInterface) {
  const history = useHistory()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })
  const [activeTabIndex, setActiveTabIndex] = React.useState(0)
  const [tabs, setTabs] = React.useState<Array<InteractiveMapTabInterface>>([])
  const [transitionEffectClass, setTransitionEffectClass] = React.useState('')
  const hasEffectRanOnceRef = React.useRef(false)

  const activeMapItems: Array<InteractiveMapItemInterface> =
    React.useMemo(() => {
      if (interactiveMapList.length < 1) {
        return []
      }

      return interactiveMapList[activeTabIndex]?.items || []
    }, [interactiveMapList, activeTabIndex])

  const isItemActives = (groupId: string): boolean =>
    interactiveMapSession &&
    interactiveMapSession.length > 0 &&
    interactiveMapSession.includes(groupId)

  const buildTab = () => {
    const filterTabs: InteractiveMapTabInterface[] = []

    interactiveMapList.forEach(
      (item: InteractiveMapListInterface, index: number) => {
        if (item.label && item.label !== '') {
          const groupIDS = [...interactiveMapSession]

          const activeItem = item.items.filter((layer) =>
            groupIDS.includes(layer.groupId)
          ).length

          filterTabs.push({ index, label: item.label, activeItem })
        }
      }
    )
    setTabs(filterTabs)
  }

  const updateFirebaseActiveCategory = async (
    label: string,
    baseImageOverride: string,
    clearInteractiveMap: boolean
  ) => {
    try {
      const payload = clearInteractiveMap
        ? {
            interactiveMap: [],
            interactiveMapActiveCategory: { label, baseImageOverride },
          }
        : { interactiveMapActiveCategory: { label, baseImageOverride } }

      await firebaseControlQuery.update(payload)
    } catch {
      throw new Error('Failed to update Firebase active category')
    }
  }

  const setCategoryBackgroundInShowcase = async (
    categoryLabel: string,
    clearInteractiveMap = true
  ) => {
    const category = interactiveMapList.find(
      (interactiveMap) => interactiveMap.label === categoryLabel
    )

    if (!category) {
      return
    }

    const hasVisibleLayer = category.items.some((item) => item.visible)

    if (hasVisibleLayer) {
      return
    }

    const hasCategoryDefault = category.items.find(
      (item) => item.categoryDefault && item.baseImageOverride
    )

    const baseImage = hasCategoryDefault
      ? hasCategoryDefault.baseImageOverride
      : ''

    const clearMap = !storyMode ? false : clearInteractiveMap

    await updateFirebaseActiveCategory(categoryLabel, baseImage, clearMap)
  }

  const handleTabChange = (index: number, label: string) => {
    if (activeTabIndex === index) {
      return
    }

    setTransitionEffectClass(
      index > activeTabIndex
        ? 'transition-slide-left'
        : 'transition-slide-right'
    )

    setActiveTabIndex(index)
    setCategoryBackgroundInShowcase(label).catch((err) => console.error(err))
  }

  const handleItemClick = async (groupId: string, index: number) => {
    let groupIDS = [...interactiveMapSession]
    if (groupIDS.includes(groupId)) {
      groupIDS = groupIDS.filter((item) => item !== groupId)
    } else {
      groupIDS = [...groupIDS, groupId]
    }
    if (storyMode) {
      groupIDS = [
        ...groupIDS.filter((i) =>
          interactiveMapList[index].items.find((j) => j.groupId === i)
        ),
      ]
    }
    await firebaseControlQuery.update({
      interactiveMap: groupIDS,
    })
  }

  useScrollEffect(activeTabIndex)

  React.useEffect(() => {
    if (interactiveMapActiveCategory) {
      const { label } = interactiveMapActiveCategory
      const categoryIndex = interactiveMapList.findIndex(
        (item) => item.label === label
      )

      const indexToSet = categoryIndex === -1 ? 0 : categoryIndex
      setActiveTabIndex(indexToSet)
    }
  }, [interactiveMapActiveCategory?.label, interactiveMapList])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const slider = document.querySelector('.slider') as HTMLDivElement
      const translateValue = `${-activeTabIndex * 100}%`
      if (slider) {
        slider.style.transform = `translateX(${translateValue})`
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [activeTabIndex])

  React.useEffect(() => {
    buildTab()
  }, [interactiveMapList, interactiveMapSession])

  React.useEffect(() => {
    if (!hasEffectRanOnceRef.current && tabs && tabs.length) {
      const activeCategoryLabel = tabs.find(
        (item) => item.index === activeTabIndex
      )?.label

      if (!activeCategoryLabel) {
        return
      }

      const clearInteractiveMap = !interactiveMapSession.length

      setCategoryBackgroundInShowcase(
        activeCategoryLabel,
        clearInteractiveMap
      ).catch((err) => console.error(err))

      hasEffectRanOnceRef.current = true
    }
  }, [tabs, activeTabIndex, interactiveMapSession])

  React.useEffect(() => {
    const handleRouteChange = async () => {
      await updateFirebaseActiveCategory('', '', false)
    }

    const unsubscribeHistory = history.listen(handleRouteChange)

    return () => {
      unsubscribeHistory()
    }
  }, [history])

  return (
    <>
      {tabs.length > 1 && (
        <InteractiveMapTab
          tabs={tabs}
          activeTabIndex={activeTabIndex}
          onTabChange={handleTabChange}
        />
      )}

      <div className="no-scrollbar h-transition-body w-full overflow-auto">
        <TransitionGroup className="h-full w-full">
          <CSSTransition
            key={activeTabIndex}
            classNames={transitionEffectClass}
            timeout={500}
            exit={false}
            onEntered={(node: HTMLElement) =>
              node.classList.remove('transition-slide-left-enter-done')
            }
          >
            <div className="flex flex-col space-y-4 px-5">
              {activeMapItems.map((itemData, index) =>
                projectIdentity.type === ProjectType.Apartment ? (
                  <InteractiveMapItem
                    key={`interactive-map-item-${index.toString()}-1`}
                    itemData={itemData}
                    onItemClick={(groupId) =>
                      handleItemClick(groupId, activeTabIndex)
                    }
                    isActive={isItemActives(itemData.groupId)}
                  />
                ) : (
                  <InteractiveMapItemHnl
                    key={`interactive-map-item-${index.toString()}`}
                    itemData={itemData}
                    onItemClick={(groupId) =>
                      handleItemClick(groupId, activeTabIndex)
                    }
                    isActive={isItemActives(itemData.groupId)}
                  />
                )
              )}
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
    </>
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(InteractiveMapHandler)
